import { SlateNode } from '@all-hands-hq/plex-graphql-schema';
import { makeStyles, MenuItem, Paper } from '@material-ui/core';
import React, { ReactNode, ReactPortal, RefObject, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Node, Range, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';

import { SearchUser } from '../../../../graphql/gen/types';
import { colorPalette } from '../../../../theme';
import { ReactEditorWithEntities, useSlate } from '../internal/useSlate';
import { UserSearchState } from './usePlexUserSearch';

export type MentionData = { target: Range | undefined; index: number };

type Props = {
  editorRef: RefObject<HTMLDivElement>;
  mentionData: MentionData;
  setMentionData: (m: MentionData) => void;
  search: UserSearchState;
};
const Portal = ({ children }: { children: ReactNode }): ReactPortal => {
  return ReactDOM.createPortal(children, document.body);
};

export const insertMention = (editor: ReactEditorWithEntities, user: SearchUser): void => {
  const mention: SlateNode = { type: 'mention', email: user.email, children: [{ text: '' }] };
  editor.setEntities((entities) => ({
    ...entities,
    mention: [...entities.mention, { email: user.email, name: user.name || user.email }],
  }));
  Transforms.insertNodes(editor, (mention as unknown) as Node);
  Transforms.move(editor);
};

export function MentionsMenu({ editorRef, mentionData, setMentionData, search }: Props): React.ReactElement | null {
  const classes = useStyles();
  const editor = useSlate();
  const { target, index } = mentionData;
  useEffect(() => {
    if (target) {
      const el = editorRef.current;
      if (!el) {
        return;
      }
      const domRange = ReactEditor.toDOMRange(editor, target);
      const rect = domRange.getBoundingClientRect();
      el.style.top = `${rect.top + window.pageYOffset + 24}px`;
      el.style.left = `${rect.left + window.pageXOffset}px`;
    }
  }, [editor, editorRef, target]);

  if (!target) {
    return null;
  }

  const isLoading = search.state === 'can_load' || search.state === 'loading';

  return (
    <Portal>
      <Paper ref={editorRef} className={classes.root}>
        {isLoading && <MenuItem disabled>Loading...</MenuItem>}
        {search.state === 'done' && search.options.length === 0 && <MenuItem disabled>No users found</MenuItem>}
        {search.state === 'done' &&
          search.options.length > 0 &&
          search.options.map((option: SearchUser, i: number) => (
            <MenuItem
              key={option.id}
              style={{ background: i === index ? colorPalette.primaryColorBlue._75 : 'transparent' }}
              onClick={(): void => {
                Transforms.select(editor, target);
                if (option) insertMention(editor, option);
                setMentionData({ ...mentionData, target: undefined });
              }}
            >
              {option.name}
            </MenuItem>
          ))}
      </Paper>
    </Portal>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    top: '-9999px',
    left: '-9999px',
    position: 'absolute',
    zIndex: 10000,
    padding: '3px',
    borderRadius: '4px',
    minWidth: 200,
    minHeight: 40,
  },
}));
